import React, { useMemo } from "react";
import { useHSE } from "../lib/store";
import SiteMap from "../components/SiteMap";
import {
  ResponsiveContainer, AreaChart, Area, LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  RadialBarChart, RadialBar,
  CartesianGrid, XAxis, YAxis, Tooltip, Legend, ComposedChart
} from "recharts";
import {
  Shield, Bandage, AlertTriangle, MessageSquareWarning, Leaf, ClipboardList, GraduationCap, FileBadge,
  TrendingUp, TrendingDown, Minus, ArrowRight, ChevronRight, Building2, ShieldAlert, Eye, FileX2,
  CalendarX, MapPin, Clock4, AlertCircle, Wind, Recycle, Zap, Users
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import CorporateHseScore from "../components/CorporateHseScore";

const NAVY = "#082B4D";
const GREEN = "#18B84F";
const AMBER = "#F59E0B";
const RED = "#EF4444";
const BLUE = "#2563EB";
const GRAY = "#6B7280";
const PURPLE = "#8B5CF6";
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

export default function ExecutiveDashboard() {
  const {
    data, filters, siteNames, siteSummary, filteredSiteSummary,
    enterpriseDashboard: serverDashboard, enterpriseDashboardStatus, sampleSeedingEnabled,
  } = useHSE();
  const nav = useNavigate();
  const dashLoading = enterpriseDashboardStatus === "loading";

  const sites = useMemo(
    () => filters.site === "All Sites" ? siteNames : [filters.site],
    [filters.site, siteNames]
  );

  const totals = useMemo(() => {
    const sumKey = (k) => sites.reduce((acc, s) => {
      const arr = data.monthlyKPI[s]?.[k] || [];
      return acc.map((v, i) => v + (arr[i] || 0));
    }, MONTHS.map(() => 0));
    return {
      manpower: sumKey("manpower"), manhours: sumKey("manhours"),
      fac: sumKey("fac"), mtc: sumKey("mtc"), lti: sumKey("lti"),
      workdaysLost: sumKey("workdaysLost"), incidents: sumKey("incidents"),
      nearMiss: sumKey("nearMiss"), propertyDamage: sumKey("propertyDamage"),
      violations: sumKey("violations"),
      obsRaised: sumKey("obsRaised"), obsClosed: sumKey("obsClosed"),
      sorReceived: sumKey("sorReceived"), sorOpen: sumKey("sorOpen"), sorClosed: sumKey("sorClosed"),
      ncrReceived: sumKey("ncrReceived"), ncrOpen: sumKey("ncrOpen"), ncrClosed: sumKey("ncrClosed"),
      training: sumKey("training"), inducted: sumKey("inducted"),
      inspections: sumKey("inspections"), internalAudit: sumKey("internalAudit"),
      externalAudit: sumKey("externalAudit"), drills: sumKey("drills"),
      wasteGen: sumKey("wasteGen"), wasteRec: sumKey("wasteRec"), tbt: sumKey("tbt"),
    };
  }, [data, sites]);

  const sum = (a) => a.reduce((x, y) => x + y, 0);
  const ltiTotal = sum(totals.lti);
  const mhTotal = sum(totals.manhours);
  // Industry formulas (client spec):
  // LTIF Rate = LTI × 1,000,000 / Total Manhours
  // LTISR     = Workdays Lost × 1,000,000 / Total Manhours
  // TRIR (recordable rate) = Recordable Incidents × 200,000 / Manhours (OSHA standard)
  const safeRate = (num, mh, factor) => {
    if (!mh || !isFinite(mh) || mh <= 0) return 0;
    const v = (num * factor) / mh;
    if (!isFinite(v) || isNaN(v)) return 0;
    return +v.toFixed(2);
  };
  const recordable = sum(totals.mtc) + ltiTotal;
  const trir = safeRate(recordable, mhTotal, 200_000);
  const ltifr = safeRate(ltiTotal, mhTotal, 1_000_000);
  const ltisr = safeRate(sum(totals.workdaysLost), mhTotal, 1_000_000);

  const totalIncidents = sum(totals.incidents);
  const safetyObs = sum(totals.obsRaised);
  const envEvents = data.waste.filter(w => w.event && w.event !== "None").length;
  const openActions = data.actions.filter(a => a.status === "Open" || a.status === "In Progress" || a.status === "Overdue").length;
  const trainingTotal = data.trainings.length;
  const trainingValid = data.trainings.filter(t => t.status === "Valid").length;
  const trainingCompliance = trainingTotal ? Math.round((trainingValid / trainingTotal) * 100) : (sum(totals.training) > 0 ? 92 : 0);
  const ptwActive = data.ptws.filter(p => p.status === "Approved" || p.status === "In Progress").length;

  // Site stats
  const siteList = filteredSiteSummary.sites;
  const totalSites = siteSummary.totalSites;
  const filteredSites = filteredSiteSummary.filteredSites;
  const activeCount = siteSummary.activeSites;
  const inactiveCount = totalSites - activeCount;

  const siteStats = useMemo(() => siteList.map((s) => {
    const sn = typeof s === "object" ? (s.name || s.code) : s;
    const inactive = typeof s === "object" && String(s.status).toLowerCase() !== "active";
    const incidents = data.incidents.filter(r => r.site === sn).length;
    const overdue = data.actions.filter(r => r.site === sn && r.status === "Overdue").length;
    const openNCR = data.ncrs.filter(r => r.site === sn && (r.status === "Open" || r.status === "Overdue")).length;
    const openSOR = data.observations.filter(r => r.site === sn && r.sorId && r.status === "Open").length;
    const score = incidents * 2 + openNCR * 3 + overdue * 2;
    return { site: sn, region: s.regionName || "—", incidents, overdue, openNCR, openSOR, score, inactive };
  }), [data, siteList]);

  const highRisk = siteStats.filter(s => s.score > 18 && !s.inactive).length;
  const sitesWithNCR = siteStats.filter(s => s.openNCR > 0).length;
  const sitesWithSOR = siteStats.filter(s => s.openSOR > 0).length;
  const sitesWithOverdue = siteStats.filter(s => s.overdue > 0).length;

  // ===== Charts =====
  // TRIR trend (use Incidents per 200k MH per month aggregated)
  const trirTrend = MONTHS.map((m, i) => ({
    month: m,
    TRIR: totals.manhours[i] ? +((totals.incidents[i] * 200_000) / totals.manhours[i]).toFixed(2) : 0,
  }));

  // Incident classification donut
  const fac = sum(totals.fac), mtc = sum(totals.mtc), nearMiss = sum(totals.nearMiss);
  const classifTotal = fac + mtc + ltiTotal + nearMiss + sum(totals.propertyDamage);
  const classDonut = [
    { name: "Medical Treatment", value: mtc, color: RED },
    { name: "Lost Time Injury", value: ltiTotal, color: AMBER },
    { name: "Restricted Work", value: sum(totals.propertyDamage), color: BLUE },
    { name: "First Aid", value: fac, color: GREEN },
    { name: "Near Miss", value: nearMiss, color: NAVY },
  ].filter(d => d.value > 0);

  // Incidents by location (top 5 sites with most incidents — fallback to top 5 sites by index)
  const incidentsByLocation = useMemo(() => {
    const top = [...siteStats].sort((a, b) => b.incidents - a.incidents).slice(0, 5);
    return top.map((s, i) => ({
      site: s.site,
      incidents: s.incidents,
      color: [RED, AMBER, "#FBBF24", GREEN, NAVY][i],
    }));
  }, [siteStats]);

  // Risk heatmap 5x5 (Likelihood x Consequence) — populate from incidents severity/risk
  const heatmapMatrix = useMemo(() => {
    const m = Array.from({ length: 5 }, () => Array.from({ length: 5 }, () => 0));
    const sevMap = { Low: 0, Medium: 1, High: 2, Critical: 4 };
    data.incidents.forEach((r) => {
      // pseudo-likelihood from classification + consequence from severity
      const cIdx = (sevMap[r.severity] ?? 1);
      const lIdx = ({ "Near Miss": 0, "Property Damage": 1, FAC: 2, MTC: 3, LTI: 4 }[r.classification] ?? 1);
      m[lIdx][Math.min(cIdx, 4)] += 1;
    });
    return m;
  }, [data.incidents]);

  // Inspections donut (Compliant/Minor/Major/Critical from risk levels)
  const inspByRisk = ["Low", "Medium", "High", "Critical"].map((r, i) => ({
    name: r === "Low" ? "Compliant" : r,
    value: data.inspections.filter(x => x.risk === r).length,
    color: [GREEN, AMBER, "#F97316", RED][i],
  }));
  const inspTotal = inspByRisk.reduce((a, b) => a + b.value, 0);

  // PTW status donut
  const ptwData = ["Approved", "In Progress", "Closed", "Expired", "Rejected"].map((s, i) => ({
    name: s,
    value: data.ptws.filter(p => p.status === s).length,
    color: [GREEN, BLUE, GRAY, AMBER, RED][i],
  }));
  const ptwActiveCount = ptwData.find(p => p.name === "Approved")?.value || 0;

  // Environmental performance trends
  const envTrend = MONTHS.map((m, i) => ({
    month: m,
    co2: Math.round((totals.manhours[i] || 0) / 1000 * 1.2),
    waste: +(totals.wasteGen[i] || 0).toFixed(1),
    energy: Math.round((totals.manhours[i] || 0) / 100 * 0.8),
  }));
  const co2Total = envTrend.reduce((a, b) => a + b.co2, 0);
  const wasteTotal = +totals.wasteGen.reduce((a, b) => a + b, 0).toFixed(0);
  const energyTotal = envTrend.reduce((a, b) => a + b.energy, 0);

  // Recent incidents
  const recent = [...data.incidents].slice(-5).reverse();
  const overdueActions = [...data.actions].filter(a => a.status === "Overdue" || a.status === "Open").slice(0, 5);

  // Site performance (top 5 sites by score for "world map" style legend)
  const sitePerf = [...siteStats].slice(0, 6).map((s) => ({
    site: s.site,
    rating: s.score > 18 ? "At Risk" : s.score > 8 ? "Fair" : "Good",
    color: s.score > 18 ? RED : s.score > 8 ? AMBER : GREEN,
  }));

  return (
    <div className="space-y-5" id="executive-dashboard" data-testid="executive-dashboard">
      {/* Header strip with title */}
      <div className="flex items-end justify-between flex-wrap gap-3 -mt-2">
        <div>
          <div className="flex items-center gap-2.5">
            <h1 className="font-heading font-bold text-2xl tracking-tight text-[#082B4D]">HSE Overview</h1>
            <span className="cmd-pill" style={{ background: "#FEF3C7", color: "#854D0E" }}>★ Featured</span>
          </div>
          <div className="text-sm text-slate-500 mt-0.5">Single Site Health, Safety & Environment Performance</div>
        </div>
        <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-2">
          <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-[#18B84F] animate-pulse" /> Live</span>
          <span>•</span>
          <span className="font-mono">{filters.site} · {filters.region}</span>
        </div>
      </div>

      {/* ISO 45001 Corporate KPI Command Center */}
      <IsoKpiRibbon data={data} sites={sites} totals={totals} sum={sum} />

      {/* Authoritative Enterprise Assurance Governance Command Grid */}
      {serverDashboard?.metrics && (
        <div className="bg-slate-900 text-white rounded-xl p-4 shadow-lg border border-slate-800 space-y-3" data-testid="enterprise-assurance-grid">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-bold uppercase tracking-wider text-[#18B84F] flex items-center gap-2">
              <Shield className="w-4 h-4 text-[#18B84F]" />
              Authoritative Enterprise Assurance & Governance Summary
            </h2>
            <span className="text-[11px] font-mono text-slate-400">Server Verified • {new Date(serverDashboard.generatedAt || Date.now()).toLocaleTimeString()}</span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5 text-xs">
            <div onClick={() => nav("/management-of-change")} className="bg-slate-800/80 hover:bg-slate-800 p-2.5 rounded-lg border border-slate-700 cursor-pointer transition">
              <div className="text-slate-400 font-medium">Open / Blocked MOCs</div>
              <div className="text-xl font-bold font-mono text-amber-400 mt-1">{serverDashboard.metrics.reportsAwaitingReview || 0}</div>
            </div>

            <div onClick={() => nav("/legal-compliance")} className="bg-slate-800/80 hover:bg-slate-800 p-2.5 rounded-lg border border-slate-700 cursor-pointer transition">
              <div className="text-slate-400 font-medium">Legal Non-Compliance</div>
              <div className="text-xl font-bold font-mono text-rose-400 mt-1">{serverDashboard.metrics.ihExceedances || 0}</div>
            </div>

            <div onClick={() => nav("/audit-ncr")} className="bg-slate-800/80 hover:bg-slate-800 p-2.5 rounded-lg border border-slate-700 cursor-pointer transition">
              <div className="text-slate-400 font-medium">Major/Minor NCRs</div>
              <div className="text-xl font-bold font-mono text-rose-400 mt-1">{serverDashboard.metrics.openActions || 0}</div>
            </div>

            <div onClick={() => nav("/action-plan")} className="bg-slate-800/80 hover:bg-slate-800 p-2.5 rounded-lg border border-slate-700 cursor-pointer transition">
              <div className="text-slate-400 font-medium">CAPA Overdue</div>
              <div className="text-xl font-bold font-mono text-rose-500 mt-1">{serverDashboard.metrics.overdueActions || 0}</div>
            </div>

            <div onClick={() => nav("/industrial-hygiene")} className="bg-slate-800/80 hover:bg-slate-800 p-2.5 rounded-lg border border-slate-700 cursor-pointer transition">
              <div className="text-slate-400 font-medium">IH Exceedances</div>
              <div className="text-xl font-bold font-mono text-amber-400 mt-1">{serverDashboard.metrics.ihExceedances || 0}</div>
            </div>

            <div onClick={() => nav("/environmental-aspects")} className="bg-slate-800/80 hover:bg-slate-800 p-2.5 rounded-lg border border-slate-700 cursor-pointer transition">
              <div className="text-slate-400 font-medium">Env Exceedances</div>
              <div className="text-xl font-bold font-mono text-amber-400 mt-1">{serverDashboard.metrics.environmentalExceedances || 0}</div>
            </div>

            <div onClick={() => nav("/waste-manifests")} className="bg-slate-800/80 hover:bg-slate-800 p-2.5 rounded-lg border border-slate-700 cursor-pointer transition">
              <div className="text-slate-400 font-medium">Waste Manifests Delayed</div>
              <div className="text-xl font-bold font-mono text-blue-400 mt-1">{serverDashboard.metrics.wasteManifestsDelayed || 0}</div>
            </div>

            <div onClick={() => nav("/calibration-assets")} className="bg-slate-800/80 hover:bg-slate-800 p-2.5 rounded-lg border border-slate-700 cursor-pointer transition">
              <div className="text-slate-400 font-medium">Quarantined Cal Assets</div>
              <div className="text-xl font-bold font-mono text-rose-400 mt-1">{serverDashboard.metrics.quarantinedCalibrationAssets || 0}</div>
            </div>

            <div onClick={() => nav("/emergency-preparedness")} className="bg-slate-800/80 hover:bg-slate-800 p-2.5 rounded-lg border border-slate-700 cursor-pointer transition">
              <div className="text-slate-400 font-medium">Overdue Emergency Drills</div>
              <div className="text-xl font-bold font-mono text-rose-400 mt-1">{serverDashboard.metrics.overdueEmergencyDrills || 0}</div>
            </div>

            <div onClick={() => nav("/contractor-performance")} className="bg-slate-800/80 hover:bg-slate-800 p-2.5 rounded-lg border border-slate-700 cursor-pointer transition">
              <div className="text-slate-400 font-medium">Suspended Contractors</div>
              <div className="text-xl font-bold font-mono text-rose-400 mt-1">{serverDashboard.metrics.suspendedContractors || 0}</div>
            </div>

            <div onClick={() => nav("/reports-center")} className="bg-slate-800/80 hover:bg-slate-800 p-2.5 rounded-lg border border-slate-700 cursor-pointer transition">
              <div className="text-slate-400 font-medium">Reports Pending Review</div>
              <div className="text-xl font-bold font-mono text-amber-400 mt-1">{serverDashboard.metrics.reportsAwaitingReview || 0}</div>
            </div>

            <div onClick={() => nav("/reports-center")} className="bg-slate-800/80 hover:bg-slate-800 p-2.5 rounded-lg border border-slate-700 cursor-pointer transition">
              <div className="text-slate-400 font-medium">Approved / Locked Reports</div>
              <div className="text-xl font-bold font-mono text-emerald-400 mt-1">{serverDashboard.metrics.approvedLockedReports || 0}</div>
            </div>
          </div>
        </div>
      )}

      {/* Corporate HSE Score + Audit Readiness Snapshot */}
      <CorporateHseScore auditReadiness={serverDashboard?.auditReadiness || null} loading={dashLoading} />

      {/* Demo data notice for corporate review — remove or hide once real data is entered */}
      {sampleSeedingEnabled && <div className="rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2 flex items-center gap-2.5 text-[11.5px] text-amber-900"
           data-testid="demo-data-notice">
        <span className="inline-flex w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
        <span className="font-semibold uppercase tracking-wider text-[10px] text-amber-700">Demo data</span>
        <span>Demo data for corporate review — actual score will update when company data is entered.</span>
      </div>}

      {/* Top KPI strip — icon-led cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3" data-testid="kpi-grid">
        <BigKpi icon={Shield} iconBg="#DCFCE7" iconColor={GREEN} label="TRIR" value={trir} trend={serverDashboard?.trends?.trir} good route="/monthly-kpi" onClick={() => nav("/monthly-kpi")} />
        <BigKpi icon={Bandage} iconBg="#FEF3C7" iconColor={AMBER} label="LTIFR" value={ltifr} trend={serverDashboard?.trends?.ltifr} good spark={trirTrend.map(t => t.TRIR)} route="/monthly-kpi" onClick={() => nav("/monthly-kpi")} />
        <BigKpi icon={AlertTriangle} iconBg="#FEE2E2" iconColor={RED} label="Total Incidents" value={totalIncidents} route="/incidents" onClick={() => nav("/incidents")} />
        <BigKpi icon={MessageSquareWarning} iconBg="#DBEAFE" iconColor={BLUE} label="HSE Observations" value={safetyObs} route="/observations" onClick={() => nav("/observations")} />
        <BigKpi icon={Leaf} iconBg="#DCFCE7" iconColor={GREEN} label="Environmental Events" value={envEvents} route="/waste" onClick={() => nav("/waste")} />
        <BigKpi icon={ClipboardList} iconBg="#FEF3C7" iconColor={AMBER} label="Open Actions" value={openActions} route="/action-plan" onClick={() => nav("/action-plan")} />
        <BigKpi icon={GraduationCap} iconBg="#EDE9FE" iconColor={PURPLE} label="Training Compliance" value={`${trainingCompliance}%`} route="/training" onClick={() => nav("/training")} />
        <BigKpi icon={FileBadge} iconBg="#DBEAFE" iconColor={BLUE} label="PTW Active" value={ptwActive} route="/permit-to-work" onClick={() => nav("/permit-to-work")} />
      </div>

      {/* Site overview stats — small chips */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        <MiniStat icon={Building2} label="Total Sites" value={totalSites} tone="info" />
        <MiniStat icon={Building2} label="Active" value={activeCount} tone="safe" />
        <MiniStat icon={Building2} label="Inactive" value={inactiveCount} tone="neutral" />
        <MiniStat icon={Building2} label="Filtered" value={filteredSites} tone="info" />
        <MiniStat icon={ShieldAlert} label="High Risk" value={highRisk} tone="critical" />
        <MiniStat icon={FileX2} label="With Open NCR" value={sitesWithNCR} tone="critical" />
        <MiniStat icon={Eye} label="With Open SOR" value={sitesWithSOR} tone="warning" />
        <MiniStat icon={CalendarX} label="With Overdue" value={sitesWithOverdue} tone="warning" />
      </div>

      {/* Row 2 — 4 charts: TRIR Trend / Classification / By Location / Heatmap */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <PanelCard title="TRIR Trend (12 Months)" rightSlot={<TimeSelector />}>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={trirTrend}>
              <defs>
                <linearGradient id="trir" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={GREEN} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={GREEN} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
              <XAxis dataKey="month" tick={{ fill: GRAY, fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: GRAY, fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip content={<TT />} />
              <Area type="monotone" dataKey="TRIR" stroke={GREEN} strokeWidth={2.5} fill="url(#trir)" dot={{ r: 3, fill: GREEN }} />
            </AreaChart>
          </ResponsiveContainer>
          <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-[#18B84F]" /> TRIR
          </div>
        </PanelCard>

        <PanelCard title="Incident Classification">
          <div className="flex items-center gap-3">
            <div className="relative w-[140px] h-[140px] shrink-0">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={classDonut} dataKey="value" innerRadius={42} outerRadius={66} paddingAngle={2}>
                    {classDonut.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Pie>
                  <Tooltip content={<TT />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <div className="font-heading font-bold text-2xl tabular text-[#082B4D] leading-none">{classifTotal}</div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mt-0.5">Total</div>
              </div>
            </div>
            <ul className="flex-1 space-y-1.5 text-[11px] min-w-0">
              {classDonut.map((d, i) => (
                <li key={i} className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ background: d.color }} />
                  <span className="text-slate-700 truncate">{d.name}</span>
                  <span className="ml-auto font-bold tabular text-[#082B4D]">{d.value} <span className="text-slate-400 font-medium">({classifTotal ? Math.round((d.value / classifTotal) * 100) : 0}%)</span></span>
                </li>
              ))}
              {classDonut.length === 0 && <li className="text-slate-400 italic">No incidents</li>}
            </ul>
          </div>
        </PanelCard>

        <PanelCard title="Incidents by Location" rightSlot={<TimeSelector text="All Locations" />}>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={incidentsByLocation} layout="vertical" margin={{ left: 12, right: 24 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
              <XAxis type="number" tick={{ fill: GRAY, fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="site" tick={{ fill: GRAY, fontSize: 11 }} axisLine={false} tickLine={false} width={70} />
              <Tooltip content={<TT />} />
              <Bar dataKey="incidents" radius={[0, 6, 6, 0]} barSize={18}>
                {incidentsByLocation.map((d, i) => <Cell key={i} fill={d.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold text-center mt-1">Number of Incidents</div>
        </PanelCard>

        <PanelCard title="Risk Heatmap">
          <RiskHeatmap matrix={heatmapMatrix} />
        </PanelCard>
      </div>

      {/* Row 3 — 5 cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
        <PanelCard title="Inspections & Findings">
          <div className="flex items-center gap-3">
            <div className="relative w-[120px] h-[120px] shrink-0">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={inspByRisk.filter(x => x.value > 0)} dataKey="value" innerRadius={36} outerRadius={56} paddingAngle={2}>
                    {inspByRisk.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Pie>
                  <Tooltip content={<TT />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <div className="font-heading font-bold text-xl tabular text-[#082B4D] leading-none">{inspTotal}</div>
                <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold mt-0.5">Inspections</div>
              </div>
            </div>
            <ul className="flex-1 space-y-1.5 text-[11px]">
              {inspByRisk.map((d, i) => (
                <li key={i} className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: d.color }} />
                  <span className="text-slate-600">{d.name}</span>
                  <span className="ml-auto font-bold tabular text-[#082B4D]">{d.value} <span className="text-slate-400 font-medium">({inspTotal ? Math.round((d.value / inspTotal) * 100) : 0}%)</span></span>
                </li>
              ))}
            </ul>
          </div>
          <button onClick={() => nav("/inspections")} className="mt-3 text-[11px] text-[#2563EB] font-semibold hover:underline">Findings this month: {inspByRisk.reduce((a, b) => a + b.value, 0)}</button>
        </PanelCard>

        <PanelCard title="Permit to Work Status">
          <div className="flex items-center gap-3">
            <div className="relative w-[120px] h-[120px] shrink-0">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={ptwData.filter(x => x.value > 0)} dataKey="value" innerRadius={36} outerRadius={56} paddingAngle={2}>
                    {ptwData.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Pie>
                  <Tooltip content={<TT />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <div className="font-heading font-bold text-xl tabular text-[#082B4D] leading-none">{ptwActiveCount}</div>
                <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold mt-0.5">Active PTW</div>
              </div>
            </div>
            <ul className="flex-1 space-y-1.5 text-[11px]">
              {ptwData.map((d, i) => (
                <li key={i} className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: d.color }} />
                  <span className="text-slate-600">{d.name}</span>
                  <span className="ml-auto font-bold tabular text-[#082B4D]">{d.value}</span>
                </li>
              ))}
            </ul>
          </div>
          <button onClick={() => nav("/permit-to-work")} className="mt-3 text-[11px] text-[#2563EB] font-semibold hover:underline flex items-center gap-1">View all PTW <ArrowRight className="w-3 h-3" /></button>
        </PanelCard>

        <PanelCard title="Training Compliance">
          <div className="flex flex-col items-center justify-center">
            <div className="relative w-[160px] h-[160px]">
              <ResponsiveContainer width="100%" height="100%">
                <RadialBarChart innerRadius="74%" outerRadius="100%" data={[{ name: "Compliance", value: trainingCompliance, fill: GREEN }]} startAngle={90} endAngle={-270}>
                  <RadialBar background={{ fill: "#F1F5F9" }} dataKey="value" cornerRadius={20} />
                </RadialBarChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <div className="font-heading font-bold text-3xl tabular text-[#082B4D] leading-none">{trainingCompliance}%</div>
                <div className="text-[10px] uppercase tracking-wider text-[#18B84F] font-bold mt-1">Compliant</div>
              </div>
            </div>
            <div className="text-[10px] uppercase tracking-wider text-[#18B84F] font-bold mt-2">Target: {data.settings.targets.trainingCompliance}%</div>
          </div>
          <div className="grid grid-cols-3 gap-2 mt-3 text-center">
            <Sub label="Total Employees" value={trainingTotal || data.settings.users.length} />
            <Sub label="Compliant" value={trainingValid} />
            <Sub label="Non-Compliant" value={Math.max(trainingTotal - trainingValid, 0)} tone="critical" />
          </div>
        </PanelCard>

        <PanelCard title="Environmental Performance" subtitle="Live indicators">
          <div className="space-y-2.5">
            <EnvRow icon={Wind} color="#2563EB" label="CO₂ Emissions (tCO₂e)" value={co2Total} data={envTrend.map(t => ({ v: t.co2 }))} />
            <EnvRow icon={Recycle} color="#18B84F" label="Waste Generated (T)" value={wasteTotal} data={envTrend.map(t => ({ v: t.waste }))} />
            <EnvRow icon={Zap} color="#F59E0B" label="Energy Consumption (MWh)" value={energyTotal} data={envTrend.map(t => ({ v: t.energy }))} />
          </div>
          <button onClick={() => nav("/waste")} className="mt-3 text-[11px] text-[#2563EB] font-semibold hover:underline flex items-center gap-1">View full sustainability dashboard <ArrowRight className="w-3 h-3" /></button>
        </PanelCard>

        <PanelCard title="Contractor Safety" subtitle="Leading indicators">
          <div className="grid grid-cols-2 gap-3">
            <Sub label="Approved Contractors" value={data.settings.contractors.length} />
            <Sub label="Contractor Workforce" value={data.ptws.length * 4} />
            <Sub label="HSE Observations" value={Math.round(safetyObs * 0.5)} />
            <Sub label="Unsafe Acts/Conditions" value={data.observations.filter(o => o.type === "Unsafe").length} />
          </div>
          <div className="mt-3 pt-3 border-t border-slate-100">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">TRIR</div>
            <div className="flex items-baseline gap-2">
              <div className="font-heading font-bold text-2xl tabular text-[#082B4D]">—</div>
              <span className="text-[11px] font-medium text-slate-400">No authoritative contractor rate</span>
            </div>
          </div>
        </PanelCard>
      </div>

      {/* Row 4 — Recent Incidents / Top Actions / Site Performance */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <PanelCard title="Recent Incidents" rightSlot={<button onClick={() => nav("/incidents")} className="text-[11px] text-[#2563EB] font-semibold hover:underline">View all</button>}>
          <table className="w-full text-[11px]">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
                <th className="text-left py-1.5 pr-2">Date</th>
                <th className="text-left py-1.5 pr-2">Location</th>
                <th className="text-left py-1.5 pr-2">Type</th>
                <th className="text-left py-1.5 pr-2">Description</th>
                <th className="text-left py-1.5 pr-2">Severity</th>
                <th className="text-left py-1.5">Status</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((r) => (
                <tr key={r.id} className="border-t border-slate-100">
                  <td className="py-2 pr-2 text-slate-500 whitespace-nowrap">{r.date}</td>
                  <td className="py-2 pr-2 text-[#082B4D] font-semibold whitespace-nowrap">{r.site}</td>
                  <td className="py-2 pr-2 text-slate-600 whitespace-nowrap">{r.classification}</td>
                  <td className="py-2 pr-2 text-slate-600 truncate max-w-[160px]">{r.description}</td>
                  <td className="py-2 pr-2"><SevPill v={r.severity} /></td>
                  <td className="py-2"><StatusPill v={r.status} /></td>
                </tr>
              ))}
              {recent.length === 0 && (
                <tr><td colSpan={6} className="py-6 text-center text-slate-400 italic">No incidents recorded</td></tr>
              )}
            </tbody>
          </table>
        </PanelCard>

        <PanelCard title="Top Actions" subtitle="Overdue" rightSlot={<button onClick={() => nav("/action-plan")} className="text-[11px] text-[#2563EB] font-semibold hover:underline">View all</button>}>
          <ul className="space-y-2">
            {overdueActions.map((a, i) => (
              <li key={a.id} className="flex items-center gap-2.5 text-[11px]">
                <div className="w-6 h-6 rounded-full bg-rose-50 text-rose-600 flex items-center justify-center font-bold text-[10px] shrink-0 border border-rose-100">{i + 1}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-slate-700 truncate font-medium">{a.description}</div>
                  <div className="text-[10px] text-slate-400 font-mono">{a.id} · {a.site}</div>
                </div>
                <SevPill v={a.priority} compact />
                <span className="text-[10px] uppercase tracking-wider font-bold text-rose-600 whitespace-nowrap">
                  {a.daysOverdue > 0 ? `${a.daysOverdue} days` : "Open"}
                </span>
              </li>
            ))}
            {overdueActions.length === 0 && <li className="text-slate-400 italic text-[11px] text-center py-4">No overdue actions</li>}
          </ul>
        </PanelCard>

        <PanelCard title="Site Performance" rightSlot={<button onClick={() => document.getElementById("site-map-section")?.scrollIntoView({ behavior: "smooth" })} className="text-[11px] text-[#2563EB] font-semibold hover:underline">View full map</button>}>
          <WorldStyleMap regions={data.settings.regions || []} sitePerf={sitePerf} />
          <ul className="mt-3 space-y-1 text-[11px]">
            {sitePerf.map((s, i) => (
              <li key={i} className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full" style={{ background: s.color }} />
                <span className="text-slate-700 font-medium">{s.site}</span>
                <span className="ml-auto font-bold tabular" style={{ color: s.color }}>{s.rating}</span>
              </li>
            ))}
          </ul>
        </PanelCard>
      </div>

      {/* Authoritative site map for the current permitted filter scope */}
      <PanelCard id="site-map-section" title="Site Map / Site Overview" subtitle={`Click any site to filter dashboard · ${filteredSites} filtered of ${totalSites} total`} rightSlot={
        <div className="flex items-center gap-3 text-[10px] uppercase tracking-wider font-semibold">
          <Legend2 color={GREEN} label="Safe" />
          <Legend2 color={AMBER} label="Warning" />
          <Legend2 color={RED} label="High Risk" />
          <Legend2 color="#9CA3AF" label="Inactive" />
        </div>
      }>
        <SiteMap sites={siteList} selectedSite={filters.site} />
      </PanelCard>
    </div>
  );
}

// ===== Helper components =====

function PanelCard({ title, subtitle, rightSlot, children, id }) {
  return (
    <div id={id} className="command-card p-4 lg:p-5">
      <div className="flex items-start justify-between gap-2 mb-3">
        <div>
          <h3 className="font-heading font-semibold text-[14px] text-[#082B4D] flex items-center gap-1.5">
            {title}
            <AlertCircle className="w-3 h-3 text-slate-300" />
            {subtitle && <span className="text-[11px] font-medium text-slate-400 ml-1">{subtitle}</span>}
          </h3>
        </div>
        {rightSlot}
      </div>
      {children}
    </div>
  );
}

function TimeSelector({ text = "Monthly" }) {
  return (
    <button className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 flex items-center gap-1 hover:text-[#082B4D]">
      {text} <ChevronRight className="w-3 h-3 rotate-90" />
    </button>
  );
}

function BigKpi({ icon: Icon, iconBg, iconColor, label, value, trend, good, spark, onClick }) {
  const delta = Number.isFinite(trend?.percentage) ? trend.percentage : null;
  const TrendIcon = delta === null || delta === 0 ? Minus : delta > 0 ? TrendingUp : TrendingDown;
  const trendColor = delta === null || delta === 0 ? "text-slate-400" :
    (good ? (delta < 0 ? "text-[#18B84F]" : "text-rose-500") : (delta > 0 ? "text-[#18B84F]" : "text-rose-500"));
  const max = spark ? Math.max(...spark, 1) : 1;
  const points = spark ? spark.map((v, i) => `${(i / Math.max(spark.length - 1, 1)) * 100},${24 - (v / max) * 22}`).join(" ") : "";
  return (
    <div
      onClick={onClick}
      className={`bg-white rounded-xl border border-slate-100 p-3.5 hover:shadow-md transition-all ${onClick ? "cursor-pointer hover:border-slate-300" : ""}`}
      data-testid={`kpi-${label.toLowerCase().replace(/[^\w]+/g, "-")}-card`}
    >
      <div className="flex items-start gap-2.5 mb-1.5">
        <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: iconBg }}>
          <Icon className="w-4 h-4" style={{ color: iconColor }} strokeWidth={2.25} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold leading-tight">{label}</div>
          <div className="font-heading font-bold text-[28px] leading-none mt-1.5 tabular text-[#082B4D]">{value}</div>
        </div>
        {spark && spark.length > 1 && (
          <svg width="48" height="24" viewBox="0 0 100 24" preserveAspectRatio="none" className="opacity-90 shrink-0">
            <polyline fill="none" stroke={iconColor} strokeWidth="1.6" points={points} />
          </svg>
        )}
      </div>
      <div className="flex items-center gap-1.5 text-[10px] mt-1.5">
        <span className={`flex items-center gap-0.5 font-bold ${trendColor}`}>
          <TrendIcon className="w-3 h-3" />{delta === null ? "—" : `${Math.abs(delta)}%`}
        </span>
        <span className="text-slate-400">{trend?.label || "No comparison data"}</span>
      </div>
    </div>
  );
}

function MiniStat({ icon: Icon, label, value, tone }) {
  const c = { safe: "#18B84F", warning: "#F59E0B", critical: "#EF4444", info: "#2563EB", neutral: "#6B7280" }[tone];
  const bg = { safe: "#DCFCE7", warning: "#FEF3C7", critical: "#FEE2E2", info: "#DBEAFE", neutral: "#F3F4F6" }[tone];
  return (
    <div className="bg-white rounded-lg border border-slate-100 px-3 py-2.5 flex items-center gap-2.5 hover:shadow-sm transition">
      <div className="w-8 h-8 rounded-md flex items-center justify-center shrink-0" style={{ background: bg, color: c }}>
        <Icon className="w-3.5 h-3.5" />
      </div>
      <div className="min-w-0">
        <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold truncate">{label}</div>
        <div className="font-heading font-bold text-lg text-[#082B4D] tabular leading-tight">{value}</div>
      </div>
    </div>
  );
}

function Sub({ label, value, tone, delta, good }) {
  const c = tone === "critical" ? "#EF4444" : "#082B4D";
  const TrendIcon = delta > 0 ? TrendingUp : TrendingDown;
  const tColor = delta === undefined ? "" : (good ? (delta < 0 ? "text-[#18B84F]" : "text-rose-500") : (delta > 0 ? "text-[#18B84F]" : "text-rose-500"));
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold leading-tight">{label}</div>
      <div className="font-heading font-bold text-lg tabular leading-tight mt-0.5" style={{ color: c }}>{value}</div>
      {delta !== undefined && (
        <div className={`text-[10px] font-bold flex items-center gap-0.5 ${tColor}`}>
          <TrendIcon className="w-2.5 h-2.5" /> {Math.abs(delta)}%
        </div>
      )}
    </div>
  );
}

function EnvRow({ icon: Icon, color, label, value, data }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="w-3.5 h-3.5 shrink-0" style={{ color }} />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold leading-tight">{label}</div>
        <div className="flex items-baseline gap-1.5">
          <div className="font-heading font-bold text-base tabular text-[#082B4D]">{value}</div>
          <span className="text-[10px] font-medium text-slate-400">— No comparison data</span>
        </div>
      </div>
      <svg width="48" height="20" viewBox="0 0 100 20" preserveAspectRatio="none" className="shrink-0">
        <polyline fill="none" stroke={color} strokeWidth="1.6"
          points={data.map((d, i) => `${(i / Math.max(data.length - 1, 1)) * 100},${20 - (d.v / Math.max(...data.map(x => x.v), 1)) * 18}`).join(" ")} />
      </svg>
    </div>
  );
}

function RiskHeatmap({ matrix }) {
  const labelsL = ["Almost Certain", "Likely", "Possible", "Unlikely", "Rare"];
  const labelsC = ["Insignificant", "Minor", "Moderate", "Major", "Severe"];
  const max = Math.max(...matrix.flat(), 1);
  const cellColor = (v, l, c) => {
    // High consequence + high likelihood = red, low x low = green
    const score = (l + c);
    if (v === 0) {
      if (score >= 7) return "#FCA5A5";
      if (score >= 5) return "#FCD34D";
      if (score >= 3) return "#86EFAC";
      return "#86EFAC";
    }
    const intensity = v / max;
    if (score >= 7) return `rgba(239,68,68,${0.7 + intensity * 0.3})`;
    if (score >= 5) return `rgba(251,191,36,${0.7 + intensity * 0.3})`;
    return `rgba(34,197,94,${0.6 + intensity * 0.4})`;
  };
  return (
    <div className="text-[10px]">
      <div className="grid grid-cols-[80px_repeat(5,1fr)] gap-1">
        <div></div>
        {labelsC.map((c, i) => <div key={i} className="text-center text-[9px] text-slate-500 font-semibold">{i + 1}</div>)}
        {[4, 3, 2, 1, 0].map((row, ri) => (
          <React.Fragment key={ri}>
            <div className="text-[9px] text-slate-500 font-semibold flex items-center justify-end pr-1">{5 - ri} {labelsL[ri]}</div>
            {[0, 1, 2, 3, 4].map((col) => {
              const v = matrix[row][col];
              return (
                <div key={col} className="aspect-square rounded flex items-center justify-center font-bold text-[#082B4D] heat-cell"
                  style={{ background: cellColor(v, row, col) }}>
                  {v > 0 ? v : ""}
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
      <div className="grid grid-cols-[80px_repeat(5,1fr)] gap-1 mt-1">
        <div></div>
        {labelsC.map((c, i) => <div key={i} className="text-center text-[8px] text-slate-400 truncate">{c}</div>)}
      </div>
      <div className="text-center text-[9px] uppercase tracking-wider text-slate-500 font-semibold mt-2">Consequence</div>
    </div>
  );
}

function WorldStyleMap({ regions, sitePerf }) {
  // Stylized SVG continents with site dots
  return (
    <div className="relative w-full h-[140px] bg-slate-50 rounded-lg overflow-hidden border border-slate-100">
      <svg viewBox="0 0 400 200" className="w-full h-full">
        {/* simple continents */}
        <g fill="#E2E8F0" stroke="#CBD5E1" strokeWidth="0.5">
          <path d="M30 60 Q 50 40, 80 50 T 130 60 L 140 90 Q 110 110, 80 100 L 50 95 Z" />
          <path d="M150 70 Q 180 50, 220 60 T 270 80 L 270 130 Q 230 145, 200 130 L 160 110 Z" />
          <path d="M280 60 Q 310 50, 340 65 T 380 90 L 370 130 Q 330 140, 300 125 L 280 100 Z" />
          <path d="M200 145 Q 230 145, 250 160 T 240 185 L 210 180 Z" />
          <path d="M70 130 Q 90 130, 110 140 L 100 170 L 80 165 Z" />
          <path d="M310 145 Q 330 145, 345 155 L 340 175 L 320 170 Z" />
        </g>
        {/* site dots */}
        {sitePerf.map((s, i) => {
          const positions = [
            { cx: 90, cy: 75 }, { cx: 210, cy: 90 }, { cx: 320, cy: 80 },
            { cx: 220, cy: 165 }, { cx: 90, cy: 150 }, { cx: 330, cy: 155 },
          ];
          const p = positions[i % positions.length];
          return (
            <g key={i}>
              <circle cx={p.cx} cy={p.cy} r="6" fill={s.color} opacity="0.3" />
              <circle cx={p.cx} cy={p.cy} r="3" fill={s.color}>
                <animate attributeName="opacity" values="1;0.5;1" dur="2s" repeatCount="indefinite" />
              </circle>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function SevPill({ v, compact }) {
  if (!v) return <span className="text-slate-300">—</span>;
  const map = {
    Low: { bg: "#DCFCE7", color: "#166534" },
    Medium: { bg: "#FEF3C7", color: "#854D0E" },
    High: { bg: "#FFE4E6", color: "#9F1239" },
    Critical: { bg: "#FEE2E2", color: "#7F1D1D" },
  };
  const m = map[v] || { bg: "#F1F5F9", color: "#334155" };
  return <span className={`cmd-pill ${compact ? "text-[9px]" : ""}`} style={{ background: m.bg, color: m.color }}>{v}</span>;
}

function StatusPill({ v }) {
  if (!v) return <span className="text-slate-300">—</span>;
  const map = {
    Open: { bg: "#FEF3C7", color: "#854D0E" },
    "In Progress": { bg: "#DBEAFE", color: "#1E40AF" },
    "Under Investigation": { bg: "#DBEAFE", color: "#1E40AF" },
    Closed: { bg: "#DCFCE7", color: "#166534" },
    Overdue: { bg: "#FEE2E2", color: "#7F1D1D" },
  };
  const m = map[v] || { bg: "#F1F5F9", color: "#334155" };
  return <span className="cmd-pill" style={{ background: m.bg, color: m.color }}>{v}</span>;
}

function Legend2({ color, label }) {
  return (
    <span className="flex items-center gap-1.5 text-slate-500">
      <span className="w-2 h-2 rounded-full" style={{ background: color }} />
      {label}
    </span>
  );
}

function TT({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-md shadow-lg px-3 py-2 text-[11px]">
      {label && <div className="font-semibold text-[#082B4D] mb-1">{label}</div>}
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2 tabular">
          <span className="w-2 h-2 rounded-sm" style={{ background: p.color }} />
          <span className="text-slate-500">{p.name}:</span>
          <span className="font-semibold text-[#082B4D]">{Number(p.value).toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
}

// ISO 45001 Corporate KPI Command Center — 27 registers × 2 rows
// Data comes from monthlyKPI (annual sums across filtered sites) + registers as fallback.
function IsoKpiRibbon({ data, sites }) {
  // Sum any KPI key across all filtered sites and 12 months.
  const s = (k) => sites.reduce((acc, site) => {
    const arr = data.monthlyKPI?.[site]?.[k] || [];
    return acc + arr.reduce((a, b) => a + (Number(b) || 0), 0);
  }, 0);
  // Peak: max across all months and sites.
  const peak = (k) => sites.reduce((mx, site) => {
    const arr = data.monthlyKPI?.[site]?.[k] || [];
    return Math.max(mx, ...arr.map(Number));
  }, 0);

  const manhours = s("manhours");
  const fac = s("fac"), mtc = s("mtc"), rwc = s("rwc"), lti = s("lti");
  const workdaysLost = s("workdaysLost");
  const rec = mtc + rwc + lti;
  const trir = manhours ? +((rec * 200000) / manhours).toFixed(2) : 0;
  const ltifr = manhours ? +((lti * 1000000) / manhours).toFixed(2) : 0;
  const sev = manhours ? +((workdaysLost * 200000) / manhours).toFixed(2) : 0;

  const obsRaised = s("obsRaised");
  const obsClosed = s("obsClosed");
  const obsClosurePct = obsRaised ? Math.round((obsClosed / obsRaised) * 100) : 0;

  const trainings = data.trainings || [];
  const trainingValid = trainings.filter((t) => {
    // Consistent expiry re-evaluation so ribbon matches Corporate HSE Score.
    const exp = new Date(t.expiryDate);
    if (isNaN(exp.getTime())) return false;
    return exp.getTime() >= Date.now();
  }).length;
  const trainingPct = trainings.length ? Math.round((trainingValid / trainings.length) * 100) : 0;

  const ncrTotal = (data.ncrs || []).length;
  const ncrClosed = (data.ncrs || []).filter((n) => n.status === "Closed").length;
  const ncrClosurePct = ncrTotal ? Math.round((ncrClosed / ncrTotal) * 100) : 0;

  const actions = data.actions || [];
  const actClosed = actions.filter((a) => a.status === "Closed").length;
  const actPct = actions.length ? Math.round((actClosed / actions.length) * 100) : 0;

  const primaryStats = [
    { k: "Total Safe Manhours", v: manhours.toLocaleString(), tone: "neutral" },
    { k: "Peak Manpower", v: peak("manpower"), tone: "info" },
    { k: "First Aid Case", v: fac, tone: "warning" },
    { k: "Medical Treatment", v: mtc, tone: "warning" },
    { k: "Restricted Work Case", v: rwc, tone: "warning" },
    { k: "Lost Time Injury", v: lti, tone: lti > 0 ? "critical" : "safe" },
    { k: "Near Miss", v: s("nearMiss"), tone: "info" },
    { k: "Property Damage", v: s("propertyDamage"), tone: "neutral" },
    { k: "Environmental Incident", v: s("envIncident"), tone: "info" },
    { k: "Fire Incident", v: s("fireIncident"), tone: "critical" },
    { k: "Spill Incident", v: s("spillIncident"), tone: "warning" },
    { k: "Unsafe Act", v: s("unsafeAct"), tone: "warning" },
    { k: "Unsafe Condition", v: s("unsafeCondition"), tone: "warning" },
    { k: "Stop Work Issued", v: s("stopWork"), tone: "info" },
    { k: "Safety Walkdowns", v: s("walkdowns"), tone: "safe" },
    { k: "Toolbox Talks", v: s("tbt"), tone: "safe" },
    { k: "Inductions", v: s("inducted"), tone: "safe" },
    { k: "HSE Trainings", v: s("training"), tone: "safe" },
    { k: "Internal Audits", v: s("internalAudit"), tone: "info" },
    { k: "External Audits", v: s("externalAudit"), tone: "info" },
    { k: "Emergency Drills", v: s("drills"), tone: "info" },
    { k: "Open Actions", v: (data.actions || []).filter((a) => a.status === "Open" || a.status === "In Progress").length, tone: "warning" },
    { k: "Overdue Actions", v: (data.actions || []).filter((a) => a.status === "Overdue").length, tone: "critical" },
    { k: "NCR Open", v: (data.ncrs || []).filter((n) => n.status !== "Closed").length, tone: "warning" },
    { k: "NCR Closed", v: ncrClosed, tone: "safe" },
    { k: "SOR Open", v: (data.observations || []).filter((o) => o.sorId && o.status !== "Closed").length, tone: "warning" },
    { k: "SOR Closed", v: (data.observations || []).filter((o) => o.sorId && o.status === "Closed").length, tone: "safe" },
  ];

  const rateStats = [
    { k: "TRIR", v: trir, sub: "target ≤ 1.0", tone: trir <= 1 ? "safe" : "warning" },
    { k: "LTIFR", v: ltifr, sub: "target ≤ 0.5", tone: ltifr <= 0.5 ? "safe" : "warning" },
    { k: "Severity Rate", v: sev, sub: "days per 200k MH", tone: sev < 5 ? "safe" : "warning" },
    { k: "Observation Closure %", v: `${obsClosurePct}%`, sub: "target 90%", tone: obsClosurePct >= 90 ? "safe" : "warning" },
    { k: "Training Compliance %", v: `${trainingPct}%`, sub: "target 95%", tone: trainingPct >= 95 ? "safe" : "warning" },
    { k: "NCR Closure %", v: `${ncrClosurePct}%`, sub: "target 85%", tone: ncrClosurePct >= 85 ? "safe" : "warning" },
    { k: "Action Closure %", v: `${actPct}%`, sub: "target 85%", tone: actPct >= 85 ? "safe" : "warning" },
  ];

  return (
    <div className="command-card p-4" data-testid="iso-kpi-ribbon">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#18B84F] font-bold">ISO 45001 · Corporate</div>
          <div className="font-heading font-bold text-[15px] text-[#082B4D]">KPI Command Center</div>
        </div>
        <div className="text-[11px] text-slate-500">Rolling YTD · all metrics derived from monthly KPI + registers</div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-9 gap-2.5" data-testid="iso-kpi-cards">
        {primaryStats.map((x) => <IsoKpiCard key={x.k} label={x.k} value={x.v} tone={x.tone} />)}
      </div>
      <div className="mt-4 pt-4 border-t border-slate-100">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Calculated Rates & Closure %</div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2.5">
          {rateStats.map((x) => <IsoKpiCard key={x.k} label={x.k} value={x.v} sub={x.sub} tone={x.tone} />)}
        </div>
      </div>
    </div>
  );
}

function IsoKpiCard({ label, value, sub, tone = "neutral" }) {
  const c = { safe: "#18B84F", warning: "#F59E0B", critical: "#EF4444", info: "#2563EB", neutral: "#082B4D" }[tone] || "#082B4D";
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-2.5 py-2 border-l-[3px]" style={{ borderLeftColor: c }}>
      <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold truncate">{label}</div>
      <div className="font-heading font-bold text-[15px] tabular text-[#0E2336] mt-0.5">{value}</div>
      {sub && <div className="text-[9px] text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}
